import { MessageContentData, MessageInfo } from '@/api/types/chat';
import { CryptoUtils } from '../crypto-utils';
import { EncryptionKey } from '@/api/types/user';
import { tabStateManager } from '../tabStateManager';
import ToastUtils from '../toast-utils';

/**
 * 消息处理工具类
 * 提供消息加密、解密和处理功能
 */
export class MessageUtils {

  /**
   * 加密消息内容
   * @param messageContent 消息内容对象
   * @param encryptionKey 加密密钥
   * @returns 加密后的数据
   */
  static async encryptMessageContent(
    messageContent: MessageContentData,
    encryptionKey: EncryptionKey
  ): Promise<{ content: string, iv: string, auth_tag: string, key_id: string, algorithm: string }> {
    try {
      // 将消息内容序列化为JSON字符串
      const contentJson = JSON.stringify(messageContent);

      // 使用CryptoUtils进行加密
      const encrypted = await CryptoUtils.encryptMessage(contentJson, encryptionKey);

      return {
        content: encrypted.content,
        iv: encrypted.iv,
        key_id: encrypted.key_id,
        auth_tag: encrypted.authTag,
        algorithm: encryptionKey.algorithm
      };
    } catch (error) {
      console.error('加密消息失败:', error);
      throw new Error('加密消息失败');
    }
  }

  /**
   * 解密消息内容
   * @param message 消息对象
   * @returns 解密后的消息内容
   */
  static async decryptMessageContent(message: MessageInfo, encryptionKey: EncryptionKey): Promise<string> {
    try {
      // 检查是否有解密所需的字段
      if (!message.content || !message.iv || !message.auth_tag) {
        return message.content || '[消息内容为空]';
      }

      // 直接使用固定的解密密钥
      const decryptionKey = encryptionKey;

      // 解密消息
      const decryptedContent = await CryptoUtils.decryptMessage(
        message.content,
        message.iv,
        message.auth_tag,
        decryptionKey
      );

      return decryptedContent;
    } catch (error) {
      console.error('解密消息失败:', error);
      return JSON.stringify(
        {
          content: '[解密失败]',
        }
      );
    }
  }

  /**
   * 批量解密消息列表
   * @param messages 消息列表
   * @returns 解密后的消息列表（带解密内容的新字段）
   */
  static async decryptMessages(messages: MessageInfo[]): Promise<MessageInfo[]> {
    const encryptionKey = tabStateManager.getEncryptionKey();
    if (!encryptionKey) {
      ToastUtils.showToastFail('当前登录状态异常，请退出后再次登录', 8000);
      return [];
    }

    const decryptedMessages = await Promise.all(
      messages.map(async (message) => {
        const decryptedContent = await this.decryptMessageContent(message, encryptionKey);

        let decryptedContentData = JSON.parse(decryptedContent) as MessageContentData;

        return {
          ...message,
          content: decryptedContent,
          decryptedContentData,
          messageType: decryptedContentData?.messageType || 'TEXT',
        };
      })
    );

    return decryptedMessages;
  }

  /**
   * 格式化消息时间
   * @param messageIdTime 消息时间字符串
   * @returns 格式化后的时间字符串
   */
  static formatMessageTime(messageIdTime: string): string {
    try {
      const timestamp = new Date(messageIdTime).getTime();
      return CryptoUtils.formatMessageTime(timestamp);
    } catch (error) {
      console.error('时间格式化失败:', error);
      return '';
    }
  }

  /**
   * 判断消息是否需要解密
   * @param message 消息对象
   * @returns 是否需要解密
   */
  static needsDecryption(message: MessageInfo): boolean {
    return !!(message.content && message.iv && message.auth_tag);
  }
}

export default MessageUtils;
